import { Box, Button, CircularProgress, Paper, Popper, Typography } from "@mui/material";
import React, { useState } from "react";

const THUMB_HEIGHT = 120;
const THUMB_MAX_WIDTH = 200;

// Grey 4:3 tile with the angle name, used when the real image cannot be loaded
// (e.g. signed URLs not reachable from this environment).
const placeholder = (angle) =>
  `data:image/svg+xml;utf8,${encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="160" height="${THUMB_HEIGHT}">` +
      `<rect width="100%" height="100%" rx="6" fill="#dcdcdc"/>` +
      `<text x="50%" y="52%" dominant-baseline="middle" text-anchor="middle" font-family="sans-serif" font-size="13" fill="#666">${angle}</text>` +
      `</svg>`
  )}`;

const Thumb = ({ img }) => {
  const [failed, setFailed] = useState(false);
  return (
    <img
      src={failed || !img.url ? placeholder(img.angle) : img.url}
      alt={img.angle}
      onError={() => setFailed(true)}
      // contain, not cover: jaw crops have varying aspect ratios and must not be cut
      style={{ height: THUMB_HEIGHT, maxWidth: THUMB_MAX_WIDTH, objectFit: "contain", borderRadius: 6, display: "block" }}
    />
  );
};

// Hover card with a scan's angles, opened from the Preview button. No backdrop, so the
// pointer can move from the button into the card. images: null while loading, [] when none.
const ScanThumbnails = ({ anchorEl, images, onOpen, onMouseEnter, onMouseLeave }) => (
  <Popper
    // Remount once images arrive so the card is positioned at its final size.
    key={images === null ? "loading" : "loaded"}
    open={Boolean(anchorEl)}
    anchorEl={anchorEl}
    placement="left-start"
    modifiers={[
      { name: "preventOverflow", options: { padding: 8 } },
      { name: "flip", options: { fallbackPlacements: ["left", "bottom-end", "top-end"] } },
      { name: "offset", options: { offset: [0, 8] } },
    ]}
    sx={{ zIndex: (theme) => theme.zIndex.tooltip }}
  >
    <Paper elevation={6} sx={{ p: 1.5, maxWidth: "90vw" }} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
      {images === null ? (
        <Box minHeight={THUMB_HEIGHT} minWidth={200} display="flex" alignItems="center" justifyContent="center">
          <CircularProgress size={24} />
        </Box>
      ) : images.length === 0 ? (
        <Typography variant="body2" color="textSecondary">
          No images
        </Typography>
      ) : (
        <Box>
          {/* One horizontal strip; many angles scroll sideways instead of stacking. */}
          <Box display="flex" gap={1.5} flexWrap="nowrap" sx={{ overflowX: "auto", pb: 0.5 }}>
            {images.map((img, i) => (
              <Box key={`${img.angle}-${i}`} textAlign="center" flexShrink={0}>
                <Thumb img={img} />
                <Typography variant="caption" display="block" sx={{ mt: 0.5, lineHeight: 1.2 }} noWrap>
                  {img.angle}
                </Typography>
                <Typography
                  variant="caption"
                  display="block"
                  sx={{ color: img.findingsCount > 0 ? "green" : "text.disabled", fontWeight: img.findingsCount > 0 ? "bold" : "normal" }}
                >
                  {img.findingsCount > 0 ? `${img.findingsCount} found` : "none"}
                </Typography>
              </Box>
            ))}
          </Box>
          {onOpen && (
            <Box display="flex" justifyContent="flex-end" mt={1}>
              <Button size="small" variant="contained" onClick={onOpen}>
                Open scan
              </Button>
            </Box>
          )}
        </Box>
      )}
    </Paper>
  </Popper>
);

export default ScanThumbnails;
